import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, ArrowRight, BarChart3, Users, Mail, Calendar } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/2457284/pexels-photo-2457284.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-indigo-900/80"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              LeadFlow
            </span>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Convert More Leads<br />Into Customers
              </h1>
              <p className="text-lg text-indigo-100 max-w-md">
                Manage your advertising funnels, automate follow-ups, and close more deals with AI-powered lead management.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <BarChart3 className="w-8 h-8 mb-3 text-indigo-300" />
                <h3 className="font-semibold mb-1">Pipeline Analytics</h3>
                <p className="text-sm text-indigo-200">Track conversions and revenue in real-time</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <Users className="w-8 h-8 mb-3 text-indigo-300" />
                <h3 className="font-semibold mb-1">Lead Scoring</h3>
                <p className="text-sm text-indigo-200">AI-powered lead prioritization</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <Mail className="w-8 h-8 mb-3 text-indigo-300" />
                <h3 className="font-semibold mb-1">Automated Follow-ups</h3>
                <p className="text-sm text-indigo-200">Email & SMS sequences that convert</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <Calendar className="w-8 h-8 mb-3 text-indigo-300" />
                <h3 className="font-semibold mb-1">Project Scheduling</h3>
                <p className="text-sm text-indigo-200">Calendar & Kanban for your team</p>
              </div>
            </div>
          </div>

          <p className="text-sm text-indigo-200">
            Trusted by 1,000+ businesses to manage their leads
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              LeadFlow
            </span>
          </div>

          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back
            </h2>
            <p className="mt-2 text-slate-600">
              Sign in to your account to continue
            </p>
          </div>

          <div className="space-y-4">
            <Button
              onClick={handleGoogleLogin}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">Secure authentication</span>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500">
            By continuing, you agree to our{" "}
            <a href="#" className="text-indigo-600 hover:underline">Terms of Service</a>
            {" "}and{" "}
            <a href="#" className="text-indigo-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
